import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { ITemplate } from 'modules/risk-management/risk-assessments/components/RunRiskAssessment';
import { RiskAssessmentService } from 'services/RiskAssessmentService';
import RiskAssessmentDetail from 'modules/risk-management/risk-assessments/risk-assessments-detail/components/RiskAssessmentDetail';

interface QuickRunAssessmentProps {
  open: boolean;
  onCloseAssessment: (reload: boolean) => void;
  assessmentFor: string | undefined;
  type: 'Dataset' | 'AI';
}

const QuickRunAssessment: React.FC<QuickRunAssessmentProps> = (props) => {
  const { type, open, onCloseAssessment, assessmentFor } = props;
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate>();
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [assessmentTemplates, setAssessmentTemplates] = useState<ITemplate[]>(
    []
  );
  const [step, setStep] = useState(1);
  const [assessmentToEdit, setAssessmentToEdit] = useState<any>();

  // API Calls
  const loadAssementTemplates = (): void => {
    setIsLoadingTemplates(true);
    RiskAssessmentService.getAssessmentTemplates()
      .then((response) => {
        const { data } = response.data;
        /* eslint no-underscore-dangle: 0 */
        setAssessmentTemplates(
          data?.templates.map((d: any) => ({ ...d, id: d._id }))
        );
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingTemplates(false);
      });
  };

  // UI Actions
  const actionRunAssessment = (): void => {
    setStep(2);
    setAssessmentToEdit({
      datasets:
        type === 'Dataset'
          ? [
              {
                _id: assessmentFor,
                title: assessmentFor,
              },
            ]
          : [],
      aiSystems:
        type === 'AI'
          ? [
              {
                _id: assessmentFor,
                title: assessmentFor,
              },
            ]
          : [],
      assessmentIds: [],
      template: selectedTemplate,
      userResponses: {},
    });
  };

  // Hooks
  useEffect(() => {
    loadAssementTemplates();
  }, []);

  // UI Elements
  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={() => {
        onCloseAssessment(false);
      }}
    >
      {step === 1 && (
        <>
          <DialogTitle>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h3">Select Assessment Template</Typography>
                <Typography variant="body1">
                  Choose a template to complete a risk assessment for this
                  entity.
                </Typography>
              </Box>
              <IconButton onClick={() => onCloseAssessment(false)}>
                <HighlightOffIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {isLoadingTemplates && (
              <Box className="empty-content">
                <CircularProgress />
              </Box>
            )}
            {!isLoadingTemplates && (
              <>
                {assessmentTemplates
                  .filter((a) => a.scope === type || a.scope === 'Both')
                  .map((t) => (
                    <Box
                      style={{
                        border: '1px solid gray',
                        cursor: 'pointer',
                        borderRadius: 10,
                        padding: '10px 20px',
                        marginBottom: 10,
                        backgroundColor:
                          t.id === selectedTemplate?.id
                            ? 'lightgray'
                            : 'transparent',
                      }}
                      key={t.id}
                      onClick={() => {
                        setSelectedTemplate(t);
                      }}
                    >
                      <Typography variant="h4">{t.templateName}</Typography>
                      <Typography>Applicability: {t.scope}</Typography>
                      <Typography>{t.description}</Typography>
                    </Box>
                  ))}
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 5 }}>
            <AppPrimaryButton
              disabled={!selectedTemplate}
              className="btn-small"
              title="Run Assessment"
              onClick={actionRunAssessment}
              isLoading={isLoadingTemplates}
              loadingText="Linking"
            />
          </DialogActions>
        </>
      )}
      {step === 2 && assessmentToEdit && (
        <>
          <DialogTitle>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Typography variant="h3">Run Assessment</Typography>

              <IconButton
                onClick={() => {
                  onCloseAssessment(false);
                }}
              >
                <HighlightOffIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <Divider />

          <RiskAssessmentDetail
            guestMode={false}
            aiSystems={assessmentToEdit.aiSystems}
            datasets={assessmentToEdit.datasets}
            assessmentIds={assessmentToEdit.assessmentIds}
            templateDetail={assessmentToEdit.template}
            quickEdit
            userResponses={assessmentToEdit.userResponses}
            onFinish={(reload: boolean) => {
              onCloseAssessment(reload);
              setAssessmentToEdit(null);
            }}
          />
        </>
      )}
    </Dialog>
  );
};

export default QuickRunAssessment;
